"use client";
import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { LazyBackgroundBeams, BackgroundBeamsFallback } from "@/components/ui/LazyLoad";

export default function HeroSection() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [displayText, setDisplayText] = useState("");

  const phrases = [
    "Full Stack Developer",
    "UI/UX Designer", 
    "Problem Solver",
    "Tech Enthusiast"
  ];

  // Load animations after initial render to prioritize LCP
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Simplified typewriter effect
  useEffect(() => {
    if (!isLoaded) return;
    
    const currentText = phrases[currentPhrase];
    let timeout;
    
    const typeText = () => {
      if (displayText.length < currentText.length) {
        setDisplayText(currentText.slice(0, displayText.length + 1));
        timeout = setTimeout(typeText, 150);
      } else {
        timeout = setTimeout(() => {
          setDisplayText("");
          setCurrentPhrase((prev) => (prev + 1) % phrases.length);
        }, 2000);
      }
    };
    
    timeout = setTimeout(typeText, 100);
    return () => clearTimeout(timeout);
  }, [displayText, currentPhrase, phrases, isLoaded]);

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-x-hidden overflow-y-hidden bg-white dark:bg-[#020817]">
      {/* Lazy load background beams with fallback */}
      {isLoaded ? (
        <LazyBackgroundBeams className="absolute inset-0 w-full h-full" />
      ) : (
        <BackgroundBeamsFallback />
      )}

      <div className="relative z-10 w-full px-2 xs:px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="flex flex-col items-center space-y-10 md:space-y-16 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center w-full">
            {/* Left Column - Text Content */}
            <div className="text-center lg:text-center space-y-6 lg:space-y-10 px-0 md:px-8 lg:px-0 w-full">
              {/* Optimized heading for LCP */}
              <div className="space-y-4">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 dark:text-gray-100 mb-2 md:mb-4 break-words">
                  Hello, I am
                </h1>
                
                <div className="h-14 sm:h-16 md:h-24 flex items-center lg:justify-center justify-center w-full">
                  <span className="text-blue-600 dark:text-blue-400 text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl break-words w-full text-center lg:text-center font-bold tracking-tight">
                    {isLoaded ? (
                      <>
                        {displayText}
                        <span className="inline-block w-1 h-8 md:h-12 ml-1 bg-blue-600 dark:bg-blue-400 animate-pulse"></span>
                      </>
                    ) : (
                      "Full Stack Developer"
                    )}
                  </span>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base md:text-xl max-w-xl mx-auto mt-4 md:mt-6">
                  Crafting digital experiences that make a difference
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-center">
                <a
                  href="#about"
                  className="inline-flex items-center px-5 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-full transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/25"
                >
                  Explore My Work →
                </a>
              </div>
            </div>

            {/* Right Column - Simplified Avatar Animation */}
            <div className="hidden sm:flex justify-center items-center">
              <div className="relative w-48 h-48 xs:w-64 xs:h-64 md:w-80 md:h-80 lg:w-[400px] lg:h-[400px]">
                {/* Glowing Background - Simplified */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 dark:from-blue-500/20 dark:to-purple-500/20 rounded-full blur-[60px] md:blur-[100px]"></div>
                
                {/* Avatar Container */}
                <div className="relative w-full h-full">
                  {/* Simplified Avatar Silhouette */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg 
                      viewBox="0 0 24 24" 
                      className="w-24 h-24 xs:w-32 xs:h-32 md:w-48 md:h-48 text-blue-600/80 dark:text-blue-400/80"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                  </div>
                  
                  {/* Simple Border */}
                  <div className="absolute inset-0 rounded-full border-2 border-blue-600/20 dark:border-blue-400/20"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}